/**
 * CrewRosterPanel — who counts as crew (docs/FLOW_RECONCILIATION_PLAN.md, decision 8).
 *
 * The crew picker used to offer everyone with an internal Member record — departed staff, the
 * Bookkeeper seat, the "Alder James Accounting" placeholder. This is the cull list: one row per
 * team member, with two switches — **Crew** (may be seated on a flight) and **Retired** (no longer
 * with the firm) — and a note. Rows are stored only as exceptions (`crew_roster`,
 * database/210_crew_roster.sql); absence means eligible, so a new hire appears without any setup.
 *
 * Lives in Admin → **Crew** (Matt, 2026-09-03: "We have an admin utility with user management.
 * Doesn't it make more sense to locate it there?") — it briefly opened from the Flight Board's
 * crew picker as a modal. Two readers depend on it: the picker (`eligibleCrew(team, roster)`) and
 * the nightly scheduler's seat count (`scripts/nightly_schedule.py`, crew capacity). Saves each
 * change immediately.
 */

import { useEffect, useState } from 'react'
import { getTeamMembersWithRoles } from '../../services/members.js'
import { getCrewRoster, setCrewRoster } from '../../services/flights.js'
import { alertDialog } from '../Dialog.jsx'

// The zones a distributed crew actually sits in, plus whatever the browser knows. The studio's own is
// the blank default, so it never appears as a choice.
const COMMON_TZ = ['America/Los_Angeles', 'America/Denver', 'America/Phoenix', 'America/Chicago', 'America/New_York',
  'America/Mexico_City', 'America/Bogota', 'America/Lima', 'America/Sao_Paulo', 'America/Argentina/Buenos_Aires',
  'Europe/London', 'Europe/Lisbon', 'Europe/Madrid', 'Europe/Paris', 'Europe/Berlin', 'Asia/Manila', 'Asia/Kolkata', 'Australia/Sydney']
const TIMEZONES = (() => {
  let all = []
  try { all = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [] } catch (_) { all = [] }
  const set = new Set([...COMMON_TZ, ...all])
  return [...COMMON_TZ, ...[...set].filter(z => !COMMON_TZ.includes(z)).sort()]
})()
function tzLabel(z) {
  try {
    const off = new Intl.DateTimeFormat('en-US', { timeZone: z, timeZoneName: 'shortOffset' }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')
    return `${z.replace(/_/g, ' ')}${off ? ' · ' + off.value : ''}`
  } catch (_) { return z }
}

export default function CrewRosterPanel() {
  const [team, setTeam] = useState(null)       // null = loading
  const [roster, setRoster] = useState([])
  const [saving, setSaving] = useState(null)   // memberAirtableId mid-save
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    Promise.all([getTeamMembersWithRoles(), getCrewRoster()])
      .then(([t, r]) => { if (alive) { setTeam(t || []); setRoster(r || []) } })
      .catch(e => { if (alive) { setTeam([]); setError(e.message || 'Could not load the roster.') } })
    return () => { alive = false }
  }, [])

  const byId = {}
  for (const r of roster) byId[r.memberAirtableId] = r

  async function update(member, patch) {
    const cur = byId[member.id] || { memberAirtableId: member.id, memberName: member.name, eligible: true, retiredAt: null, note: '', timezone: null }
    const next = { ...cur, ...patch }
    setSaving(member.id)
    try {
      const row = await setCrewRoster({
        memberAirtableId: member.id, memberName: member.name,
        eligible: next.eligible !== false, retired: !!next.retiredAt, note: next.note || '',
        timezone: next.timezone || null,
      })
      const mapped = {
        memberAirtableId: row.member_airtable_id, memberName: row.member_name || member.name,
        eligible: row.eligible !== false, retiredAt: row.retired_at, note: row.note || '',
        timezone: row.timezone || null,
        updatedAt: row.updated_at, updatedBy: row.updated_by || null,
      }
      setRoster(prev => prev.filter(r => r.memberAirtableId !== member.id).concat(mapped))
    } catch (e) {
      await alertDialog({ title: 'Could not save', message: e.message || 'The roster change did not save.' })
    } finally { setSaving(null) }
  }

  const rows = (team || []).slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  const eligibleCount = rows.filter(m => { const r = byId[m.id]; return !(r && (r.retiredAt || r.eligible === false)) }).length

  return (
    <div style={S.wrap}>
      <div style={S.sub}>
        Who may be seated on a flight. Everyone with a team seat is crew unless switched off here; a
        retired person stays for history but is never offered again. The nightly scheduler counts
        seats from this same list, so an account that never flies should be switched off. The timezone
        is where the person works from: the captain's daily report asks once per their own day.
        {team && <span style={S.count}> {eligibleCount} of {rows.length} eligible.</span>}
      </div>
      {error && <div style={{ ...S.empty, color: '#b0413e' }}>{error}</div>}
      <div style={S.list}>
        {team === null && <div style={S.empty}>Loading the team…</div>}
        {team && rows.length === 0 && !error && <div style={S.empty}>No team members found.</div>}
        {rows.map(m => {
          const r = byId[m.id]
          const retired = !!(r && r.retiredAt)
          const eligible = !retired && !(r && r.eligible === false)
          const busy = saving === m.id
          return (
            <div key={m.id} style={{ ...S.row, opacity: busy ? 0.55 : 1 }}>
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <div style={{ ...S.name, color: eligible ? 'var(--aj-text)' : 'var(--aj-text-muted)', textDecoration: retired ? 'line-through' : 'none' }}>
                  {m.name}
                  {m.capacity && m.capacity.practiceRoles && <span style={S.role}> · {m.capacity.practiceRoles}</span>}
                </div>
                <input
                  type="text"
                  placeholder="Note (why, when)"
                  defaultValue={(r && r.note) || ''}
                  disabled={busy}
                  onBlur={e => { const v = e.target.value.trim(); if (v !== ((r && r.note) || '')) update(m, { note: v }) }}
                  style={S.note}
                />
              </div>
              {/* The person's own day (2026-09-16): the captain's daily report asks once per THEIR day,
                  not once per Pacific day. Blank means the studio's Pacific time. */}
              <select value={(r && r.timezone) || ''} disabled={busy} onChange={e => update(m, { timezone: e.target.value || null })}
                style={S.tz} title="Where this person works from. The captain's report asks once per their own day.">
                <option value="">Pacific (studio)</option>
                {TIMEZONES.filter(z => z !== 'America/Los_Angeles').map(z => <option key={z} value={z}>{tzLabel(z)}</option>)}
              </select>
              <label style={S.switch} title={retired ? 'Retired people are never crew' : 'May be seated on a flight'}>
                <input type="checkbox" checked={eligible} disabled={busy || retired}
                  onChange={e => update(m, { eligible: e.target.checked })} />
                <span>Crew</span>
              </label>
              <label style={S.switch} title="No longer with the firm">
                <input type="checkbox" checked={retired} disabled={busy}
                  onChange={e => update(m, { retiredAt: e.target.checked ? new Date().toISOString() : null })} />
                <span>Retired</span>
              </label>
            </div>
          )
        })}
      </div>
      <div style={S.foot}>Changes save as you make them.</div>
    </div>
  )
}

const S = {
  wrap: { fontFamily: '"DM Sans", sans-serif', maxWidth: 640 },
  sub: { fontSize: 12, color: 'var(--aj-text-muted)', lineHeight: 1.55, marginBottom: 10 },
  count: { color: 'var(--aj-text-mid)', fontFamily: '"DM Mono", monospace', fontSize: 11.5 },
  list: { borderTop: '0.5px solid var(--aj-border)' },
  row: { display: 'flex', alignItems: 'center', gap: 14, padding: '8px 0', borderBottom: '0.5px solid var(--aj-border)' },
  name: { fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  role: { fontWeight: 400, color: 'var(--aj-text-muted)', fontSize: 11 },
  note: { marginTop: 4, width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', background: 'transparent', border: 'none', borderBottom: '0.5px dashed var(--aj-border)', padding: '2px 0', outline: 'none' },
  switch: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--aj-text-mid)', cursor: 'pointer', flexShrink: 0 },
  tz: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '3px 6px', maxWidth: 190, flexShrink: 0 },
  empty: { padding: '16px 0', fontSize: 12, color: 'var(--aj-text-muted)' },
  foot: { fontSize: 11, color: 'var(--aj-text-muted)', paddingTop: 10 },
}
